import { getCurrentStationId, INIT_PAGINATION } from '@spx-portal/shared-utils';
import { useRequest } from 'ahooks';
import React from 'react';
import { ProTable } from 'react-pro-components';
import type { ProTableProps } from 'react-pro-components/typings/components/pro-table/types';
import { Button, Form, message, Modal } from 'ssc-ui-react';
import { ColumnProps } from 'ssc-ui-react/typings/components/table/types';

import { $gt } from '@/common/appCtx';
import { usePermissions } from '@/pages/shift/hooks/use-permissions';

import { transformAdminShiftListParams } from './adapter';
import * as API from './api';
import TimeSlotModificationHistory from './components/timeslot-modification-history';
import { useFields } from './fields';
import { useBatchAction } from './hooks/use-batch-action';
import { useShiftModal } from './hooks/use-shift-modal';
import { useSmallBufferConfig } from './hooks/use-small-buffer-config';

export default function List() {
  const proTableRef = React.useRef<{fetch?: () => void}>(null);
  const [logVisible, setLogVisible] = React.useState(false);
  const [pagination, setPagination] = React.useState(INIT_PAGINATION);
  const [form] = Form.useForm();
  const { showShiftModal, shiftModalComp } = useShiftModal({ onSuccess: () => proTableRef.current?.fetch?.() });
  const { showSmallBufferConfig } = useSmallBufferConfig();
  const { batchActionComp } = useBatchAction(proTableRef.current?.fetch);
  const { showColumn, queryField } = useFields({ lineClamp: 1 });
  const permission = usePermissions();

  const { loading, data, run: manualGetSlotList } = useRequest(API.getAdminShiftList, {
    manual: true,
    onSuccess(data) {
      const {
        total = 0,
        pageno = INIT_PAGINATION.current,
        count = INIT_PAGINATION.pageSize,
      } = data.data || {};
      setPagination({
        ...pagination,
        total,
        current: pageno,
        pageSize: count,
      });
    },
    onError(e) {
      console.error(e);
    },
  });

  const handleClickLogBtn = () => {
    setLogVisible(true);
  };

  const handleExport = async() => {
    const params = transformAdminShiftListParams({ model: form.getFieldsValue() });
    const query = Object.entries(params).filter(([key, value]) => value !== undefined).map(([key, value]) => `${key}=${value}`).join('&');
    window.open(`/api/wfm/admin/roster_planning/slot/admin_slot_list_download?${query}`);
  };

  const Operation = () => {
    return (
      <>
        {permission.hasCreatePermission ? (
          <>
            <Button
              type="primary"
              onClick={() => showShiftModal('create',)}
            >
              {$gt('Create')}
            </Button>
            {batchActionComp}
          </>
        ) : null}
        {permission.hasExportPermission ? (
          <Button
            onClick={handleExport}
          >
            {$gt('Export')}
          </Button>
        ) : null}
        <Button onClick={handleClickLogBtn}>{$gt('Log')}</Button>
      </>
    );
  };

  const handleDeleteShift = async(row: any, isApprove: 0 | 1 = 0) => {
    Modal.confirm({
      title: $gt('Delete Shift'),
      content: $gt('Do you want to delete this shift?'),
      onOk: async() => {
        try {
          await API.deleteShift({ slot_id: row.slot_id, is_approve: isApprove });
          message.success($gt('Delete Successfully'));
          proTableRef.current?.fetch?.();
        } catch (e: any) {
          if (e.retcode === 181401018) {
            Modal.confirm({
              title: $gt('Notice'),
              content: e.message,
            });
          }
          if (e.retcode === 181401019) {
            Modal.confirm({
              title: $gt('Notice'),
              content: e.message,
              onOk: () => {
                handleDeleteShift(row, 1);
              },
            });
          }
          console.error(e);
        }
      },
    });
  };

  const showDeleteButton = (row: any) => {
    return permission.hasDeletePermission && row.create_station_id === getCurrentStationId();
  };

  const showEditButton = () => {
    return permission.hasEditPermission;
  };

  const actionColumn = () => ({
    actions: (row: any) => {
      return [
        showEditButton() ? {
          children: $gt('Edit'),
          onClick: () => {
            showShiftModal('edit', row);
          },
        } : null,
        showDeleteButton(row) ? {
          children: $gt('Delete'),
          disabled: !row.can_delete,
          onClick: () => {
            handleDeleteShift(row);
          },
        } : null,
      ].filter(Boolean) as Array<{children: any; onClick: () => void}>;
    },
  });

  const fetchData = {
    fetcher: async(info: any) => {
      const { form, pagination: curPagination } = info;
      const { current, pageSize } = curPagination || {};
      await manualGetSlotList({
        pageno: current || INIT_PAGINATION.current,
        count: pageSize || INIT_PAGINATION.pageSize,
        ...transformAdminShiftListParams({ model: form }),
      });
    },
    fetchOnMount: true,
    fetchOnFormChange: false,
    fetchOnFormReset: true,
    fetchOnFormSubmit: true,
    fetchOnTableChange: true,
  };

  const proTableProps: ProTableProps = {
    loading,
    searchForm: {
      formProps: {
        form,
      },
      fields: Object.values(queryField),
      footerProps: { showReset: true },
      startCollapseRows: 3,
      columns: 3,
    },
    operation: <Operation />,
    table: {
      dataSource: data?.data?.list || [],
      columns: Object.values(showColumn) as ColumnProps<any>[],
      actionColumn: {
        width: 150,
        fixed: 'right',
        ...actionColumn(),
      },
      rowKey: 'slot_id',
      pagination,
      sticky: true,
      scroll: { x: true },
    },
    fetchData,
  };

  return (
    <>
      <ProTable ref={proTableRef} {...proTableProps} />
      <TimeSlotModificationHistory
        visible={logVisible}
        onClose={() => setLogVisible(false)}
      />
      {shiftModalComp}
    </>
  );
}
